/**
 * ManualSearch Component
 *
 * Search bar with dropdown results for manual/handbook.
 * Features:
 * - Debounced search using useDeferredValue
 * - Keyboard shortcuts (Cmd/Ctrl+K to focus, Escape to close)
 * - Click outside to close
 * - Loading spinner during search
 * - Clear button
 *
 * Story 2-3: ManualSearch Component
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useManualSearch, MIN_SEARCH_LENGTH } from './useManual';
import { SearchResults } from './SearchResults';
import { useClickOutside } from '../../hooks/useClickOutside';

interface ManualSearchProps {
  onNavigate: (slug: string) => void;
}

/**
 * Search icon (magnifying glass)
 */
function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

/**
 * X icon for clear button
 */
function XIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

export function ManualSearch({ onNavigate }: ManualSearchProps) {
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data, isLoading, isFetching, isError } = useManualSearch(query);

  // Close dropdown when clicking outside
  useClickOutside(containerRef, () => setIsOpen(false), isOpen);

  // Keyboard shortcut (Cmd/Ctrl + K) to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to focus search
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        inputRef.current?.select();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Handle Escape key to close dropdown
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      inputRef.current?.blur();
    }
  }, []);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    // Open dropdown when query meets minimum length
    setIsOpen(value.length >= MIN_SEARCH_LENGTH);
  }, []);

  // Handle clear button
  const handleClear = useCallback(() => {
    setQuery('');
    setIsOpen(false);
    inputRef.current?.focus();
  }, []);

  // Handle result selection
  const handleSelect = useCallback(
    (slug: string) => {
      onNavigate(slug);
      setQuery('');
      setIsOpen(false);
    },
    [onNavigate]
  );

  // Handle focus
  const handleFocus = useCallback(() => {
    if (query.length >= MIN_SEARCH_LENGTH) {
      setIsOpen(true);
    }
  }, [query]);

  // Show loading when fetching (includes initial + background refetches)
  const showLoading = isLoading || isFetching;

  return (
    <div ref={containerRef} className="relative" data-testid="manual-search">
      {/* Search input container */}
      <div className="relative">
        {/* Search icon */}
        <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />

        {/* Input field */}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder="Zoeken..."
          style={{ paddingLeft: '2.5rem' }}
          className="w-full pr-10 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls="search-results"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          data-testid="manual-search-input"
        />

        {/* Clear button */}
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none focus:text-gray-600 transition-colors"
            aria-label="Zoekopdracht wissen"
            data-testid="manual-search-clear"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div
          id="search-results"
          className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
          role="listbox"
          aria-live="polite"
          data-testid="manual-search-dropdown"
        >
          {showLoading ? (
            <div className="p-4 flex justify-center" data-testid="search-loading">
              <div
                className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full"
                role="status"
                aria-label="Zoeken..."
              />
            </div>
          ) : isError ? (
            <div
              className="p-4 text-center text-red-500"
              data-testid="search-error"
              role="alert"
            >
              Er ging iets mis bij het zoeken. Probeer het opnieuw.
            </div>
          ) : data?.data && data.data.length > 0 ? (
            <>
              <SearchResults results={data.data} onSelect={handleSelect} />
              {data.total > data.data.length && (
                <div className="px-4 py-2 text-xs text-gray-500 border-t border-gray-100 bg-gray-50">
                  Toont {data.data.length} van {data.total} resultaten
                </div>
              )}
            </>
          ) : query.length >= MIN_SEARCH_LENGTH && !showLoading ? (
            <div
              className="p-4 text-center text-gray-500"
              data-testid="search-no-results"
            >
              Geen resultaten voor &quot;{query}&quot;
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
